import { supabase } from "@/integrations/supabase/client";
import { MentorWithDetails } from "@/types/mentor";
import { getMultipleMentorRatings } from "@/services/reviewService";

// Fetch all mentors with their details that have at least 1 active mentorship offering
export const fetchMentoresWithDetails = async (): Promise<MentorWithDetails[]> => {
  try {
    console.log("Fetching mentors with details...");
    
    // First fetch mentors who have at least one active offering
    const { data: mentors, error } = await supabase
      .from("mentor_info")
      .select(`
        id,
        profile_id,
        bio,
        image_url,
        cover_url,
        is_active,
        youtube_url,
        instagram_url,
        discord_url,
        twitch_url,
        profiles:profile_id (
          id,
          nickname,
          slug
        ),
        mentorship_offerings!inner (
          id,
          is_active
        )
      `)
      .eq("is_active", true)
      .eq("mentorship_offerings.is_active", true);

    if (error) {
      console.error("Error fetching mentors:", error);
      throw new Error(`Failed to fetch mentors: ${error.message}`);
    }

    if (!mentors || mentors.length === 0) {
      console.log("No active mentors found with active offerings");
      return [];
    }

    console.log(`Fetched ${mentors.length} active mentors with active offerings`);
    
    // Log a sample to check data structure
    if (mentors.length > 0) {
      console.log("Sample mentor data:", mentors[0]);
      console.log("Sample mentor profile:", mentors[0].profiles);
    }

    // Buscar avaliações de todos os mentores de uma vez
    // IMPORTANTE: getMultipleMentorRatings espera profile_id, não mentor_info.id
    const mentorProfileIds = mentors.map(mentor => mentor.profile_id).filter(Boolean) as string[];
    console.log("📊 [fetchMentoresWithDetails] Buscando avaliações para profile_ids:", mentorProfileIds);
    const mentorRatings = await getMultipleMentorRatings(mentorProfileIds);
    console.log("📊 [fetchMentoresWithDetails] Avaliações encontradas:", mentorRatings);

    // Now we need to fetch additional data for each mentor
    const enhancedMentors = await Promise.all(
      mentors.map(async (mentor) => {
        // Extract the nickname with more detailed logging
        const profileData = mentor.profiles;
        const mentorName = profileData ? profileData.nickname : null;
        
        console.log(`Processing mentor: ${mentor.id} - Profile data:`, profileData);
        console.log(`Nickname extracted: ${mentorName}`);
        
        try {
          // Fetch offerings for this mentor (only active ones)
          const { data: offerings } = await supabase
            .from("mentorship_offerings")
            .select(`
              id, 
              price_cents,
              game_id, 
              game:game_id(id, name, cover_url)
            `)
            .eq("mentor_id", mentor.id)
            .eq("is_active", true);

          // Calculate the minimum price
          let minimumPrice = null;
          if (offerings && offerings.length > 0) {
            minimumPrice = Math.min(...offerings.map(item => item.price_cents));
          }

          // Extract unique games
          const games = offerings
            ? Array.from(
                new Map(
                  offerings
                    .filter(item => item.game)
                    .map(item => [item.game.id, item.game])
                ).values()
              )
            : [];

          // Use real rating from the review service
          // IMPORTANTE: mentorRatings usa profile_id como chave, não mentor_info.id
          const mentorRating = mentorRatings[mentor.profile_id];
          const averageRating = mentorRating ? mentorRating.averageRating : 0;
          console.log(`⭐ [fetchMentoresWithDetails] Mentor ${mentor.id} (profile_id: ${mentor.profile_id}) - Rating: ${averageRating}`);

          return {
            id: mentor.id,
            profile_id: mentor.profile_id,
            slug: profileData?.slug,
            bio: mentor.bio,
            image_url: mentor.image_url,
            cover_url: mentor.cover_url,
            is_active: mentor.is_active,
            name: mentorName && mentorName.trim() !== "" ? mentorName : `Mentor ${mentor.id.substring(0, 4)}`,
            average_rating: averageRating,
            minimum_price: minimumPrice,
            games: games,
            social: {
              youtube: mentor.youtube_url,
              instagram: mentor.instagram_url,
              discord: mentor.discord_url,
              twitch: mentor.twitch_url,
            }
          } as MentorWithDetails;
        } catch (offeringsError) {
          console.error(`Error fetching offerings for mentor ${mentor.id}:`, offeringsError);
          
          // Return mentor with basic info even if offerings fetch failed
          // IMPORTANTE: mentorRatings usa profile_id como chave, não mentor_info.id
          const mentorRating = mentorRatings[mentor.profile_id];
          const averageRating = mentorRating ? mentorRating.averageRating : 0;
          console.log(`⭐ [fetchMentoresWithDetails] Mentor ${mentor.id} (profile_id: ${mentor.profile_id}) - Rating (fallback): ${averageRating}`);
          
          return {
            id: mentor.id,
            profile_id: mentor.profile_id,
            slug: profileData?.slug,
            bio: mentor.bio,
            image_url: mentor.image_url,
            cover_url: mentor.cover_url,
            is_active: mentor.is_active,
            name: mentorName && mentorName.trim() !== "" ? mentorName : `Mentor ${mentor.id.substring(0, 4)}`,
            average_rating: averageRating,
            minimum_price: null,
            games: []
          } as MentorWithDetails;
        }
      })
    );

    console.log(`Successfully enhanced ${enhancedMentors.length} mentor profiles with active offerings`);
    console.log("Sample enhanced mentor:", enhancedMentors.length > 0 ? enhancedMentors[0] : "None");
    return enhancedMentors;
  } catch (error) {
    console.error("Error in fetchMentoresWithDetails:", error);
    throw error;
  }
};
